import {
  Card,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { DateTime } from 'luxon';
import Label from 'src/components/label';
import { SearchCard } from 'src/components/search-card';
import { SortableHeader } from 'src/components/sortable-header';
import { TruxTableCell } from 'src/components/trux-table-cell';
import { useSearch } from 'src/hooks/use-search';
import { useSort } from 'src/hooks/use-sort';
import { Trip } from '../types';
import { latenessColor, latenessText, tripStatusColor, tripStatusText } from '../trip-utils';

export type HistoryLine = {
  tripId: number;
  driverName: string;
  tripName: string;
  status: number;
  departedAt: string | null;
  endedAt: string | null;
  durationHours: number | null;
  stopsOnTimeText: string;
  onTimeRatio: number | null;
  worstLatenessHours: number | null;
  raw: Trip;
};

type TripHistoryTableProps = {
  lines: HistoryLine[];
  timezone: string;
  onSelectTrip: (trip: Trip) => void;
};

const TripHistoryTable = memo(({ lines, timezone, onSelectTrip }: TripHistoryTableProps) => {
  const { sortedList, sortData } = useSort(lines, 'endedAt', 'desc');
  const { searchText, setSearchText, filteredList } = useSearch(sortedList || [], [
    'driverName',
    'tripName',
  ]);

  const formatTime = (value: string | null) =>
    value ? DateTime.fromISO(value).setZone(timezone).toFormat('LLL d, h:mm a') : '—';

  return (
    <>
      <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="trips" />

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableHeader sortKey="driverName" sortData={sortData}>
                  Driver
                </SortableHeader>
                <SortableHeader sortKey="tripName" sortData={sortData}>
                  Trip
                </SortableHeader>
                <SortableHeader sortKey="status" sortData={sortData}>
                  Status
                </SortableHeader>
                <SortableHeader sortKey="departedAt" sortData={sortData}>
                  Departed
                </SortableHeader>
                <SortableHeader sortKey="endedAt" sortData={sortData}>
                  Ended
                </SortableHeader>
                <SortableHeader sortKey="durationHours" sortData={sortData} align="right">
                  Duration
                </SortableHeader>
                <SortableHeader sortKey="onTimeRatio" sortData={sortData} align="right">
                  Stops On-Time
                </SortableHeader>
                <SortableHeader sortKey="worstLatenessHours" sortData={sortData} align="right">
                  Worst Lateness
                </SortableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredList.map((line) => (
                <TableRow
                  key={line.tripId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onSelectTrip(line.raw)}
                >
                  <TruxTableCell>{line.driverName}</TruxTableCell>
                  <TruxTableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {line.tripName}
                    </Typography>
                  </TruxTableCell>
                  <TruxTableCell>
                    <Label color={tripStatusColor(line.status)}>{tripStatusText(line.status)}</Label>
                  </TruxTableCell>
                  <TruxTableCell>{formatTime(line.departedAt)}</TruxTableCell>
                  <TruxTableCell>{formatTime(line.endedAt)}</TruxTableCell>
                  <TruxTableCell align="right">
                    {line.durationHours !== null ? `${line.durationHours.toFixed(1)}h` : '—'}
                  </TruxTableCell>
                  <TruxTableCell align="right">{line.stopsOnTimeText}</TruxTableCell>
                  <TruxTableCell align="right">
                    {line.worstLatenessHours !== null ? (
                      <Label color={latenessColor(line.worstLatenessHours)}>
                        {latenessText(line.worstLatenessHours)}
                      </Label>
                    ) : (
                      '—'
                    )}
                  </TruxTableCell>
                </TableRow>
              ))}
              {filteredList.length === 0 && (
                <TableRow>
                  <TruxTableCell colSpan={8}>
                    <Stack alignItems="center" p={2}>
                      <Typography variant="body2" color="text.secondary">
                        No trips match the search
                      </Typography>
                    </Stack>
                  </TruxTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
});

export default TripHistoryTable;
